import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { IoArrowDown } from 'react-icons/io5';
import { Dropdown, Modal, Textarea } from 'rizzui';
import { StateOption, StateOptions } from '../../type/entities/state-type';
import cn from '../../utils/classNames';
import { CustomBadge } from './CustomBadge';
import { toast } from 'sonner';

interface StatusDropdownProps {
    entityId: string;
    currentState: any;
    currentStateDate?: string;
    statesOptions: StateOptions;
    title: string;
    dropdownClassName?: string;
    disabled?: boolean;
    badgeSize?: "sm" | "md" | "lg" | "xl" | undefined;
    badgeRounded?: "none" | "sm" | "md" | "lg" | "pill" | undefined;
    modalSize?: 'sm' | 'md' | 'lg' | 'full';
    cancelBtnText?: string;
    confirmBtnText?: string;
    handleStateChange: (invoiceId: string, newStateId: string, comment: string) => void;
    hasComments?: boolean;
    customBadgeClass?: string;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
    title,
    statesOptions = [],
    entityId,
    currentState,
    currentStateDate,
    dropdownClassName,
    disabled = false,
    badgeSize = 'lg',
    badgeRounded = 'md',
    modalSize = 'md',
    cancelBtnText = 'Cancelar',
    confirmBtnText = 'Confirmar',
    handleStateChange,
    hasComments = true,
    customBadgeClass = '',
}) => {

    const [auxCurrentState, setAuxCurrentState] = useState({
        name: currentState?.name,
        id: currentState?.id,
        color: currentState?.color
    });
    const [showModal, setShowModal] = useState(false);
    const [selectedState, setSelectedState] = useState<StateOption | null>(null);
    const [isDelete, setIsDelete] = useState<boolean>(false);
    const [commentError, setCommentError] = useState<string>('');

    // ---------------------------------------------------
    // DROPDOWN MENU ACTIONS
    // ---------------------------------------------------
    const handleStateSelect = (state: StateOption) => {
        if (state.label === 'Eliminado') {
            setIsDelete(true);
        } else {
            setIsDelete(false);
        }
        setSelectedState(state);
        setShowModal(true);
    };

    // ---------------------------------------------------
    // MODAL MENU ACTIONS
    // ---------------------------------------------------

    // ---------------------------------------------------
    /*
    * EN: Handle confirm action in the modal.
    * ES: Maneja la acción de confirmar en el modal.
    */
    // ---------------------------------------------------
    const handleConfirm = () => {
        const textareaValue = document.getElementById('comments') as HTMLInputElement;
        if (hasComments && !textareaValue?.value) {
            setCommentError('Este campo es obligatorio');
            return;
        }
        setCommentError('');
        if (selectedState) {
            handleStateChange(entityId, selectedState.value, textareaValue?.value || '');
            setAuxCurrentState({
                name: selectedState.label,
                id: selectedState.value,
                color: selectedState.color,
            });
        }
        setShowModal(false);
    };
    // ---------------------------------------------------


    // ---------------------------------------------------
    /*
    * EN: Handle cancel action in the modal.
    * ES: Maneja la acción de cancelar en el modal.
    */
    // ---------------------------------------------------
    const handleCancel = () => {
        setSelectedState(statesOptions.find((state) => state.value === auxCurrentState.id) || null);
        if (auxCurrentState.name === 'Eliminado') {
            setIsDelete(true);
        } else {
            setIsDelete(false);
        }
        setShowModal(false);
    };
    // ---------------------------------------------------

    useEffect(() => {
        if (currentState) {
            setAuxCurrentState({
                name: currentState.name,
                id: currentState.id,
                color: currentState.color,
            });
        }
    }, [currentState, statesOptions]);

    return (
        <div>
            <Dropdown placement="bottom-end" className={cn('w-full', dropdownClassName)} key={entityId}>
                <Dropdown.Trigger className='w-full'>
                    <CustomBadge
                        className={cn('w-full p-0', customBadgeClass, disabled ? 'cursor-not-allowed opacity-50' : isDelete ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
                        rounded={badgeRounded}
                        size={badgeSize}
                        customColor={selectedState?.color || auxCurrentState.color || undefined}
                    >
                        <div className="flex flex-col justify-center items-center text-nowrap leading-relaxed m-1">
                            {selectedState?.label || auxCurrentState.name || 'Seleccionar Estado'} <br />
                            {currentStateDate && (moment(currentStateDate).format('DD/MM/YY HH:mm'))}
                        </div>
                    </CustomBadge>
                </Dropdown.Trigger>
                {!disabled && !isDelete && (
                    <Dropdown.Menu className="max-h-80 md:max-h-50 w-auto overflow-auto shadow-xl">
                        {statesOptions && statesOptions.length >= 1 && auxCurrentState && statesOptions.filter((state) => state.value !== auxCurrentState.id).map((state) => (
                            <Dropdown.Item key={state.value} onClick={() => handleStateSelect(state)} className='w-full' >
                                <CustomBadge
                                    key={state.value}
                                    className={cn(customBadgeClass, "cursor-pointer w-full")}
                                    rounded={badgeRounded}
                                    size={badgeSize || 'lg'}
                                    customColor={state.color || undefined}
                                >
                                    {state.label}
                                </CustomBadge>
                            </Dropdown.Item>
                        ))}
                        {(!statesOptions || statesOptions.length === 0) && (
                            <div className='px-4 py-2'>Sin resultados...</div>
                        )}
                    </Dropdown.Menu>
                )}
            </Dropdown>

            {showModal && (
                <Modal isOpen={showModal} onClose={() => { }} size={modalSize}>
                    <div className='flex flex-col justify-center p-2 pt-4'>
                        <h4 className="text-2xl text-center">{
                        isDelete ? 'Si cambias al estado de Eliminado, se realizará la eliminación del documento y no podrás cambiar de nuevo el estado, aunque podrás seguir editando sus valores. ¿Estas seguro de continuar?' 
                        :
                         title || '¿Estas seguro de cambiar el estado?'}</h4>
                    </div>
                    <div className='flex flex-col gap-3 my-3'>
                        <CustomBadge
                            className={cn(customBadgeClass, "cursor-pointer m-auto")}
                            rounded={badgeRounded}
                            size={badgeSize}
                            customColor={auxCurrentState?.color || undefined}
                        >
                            {auxCurrentState.name || 'Seleccionar Estado'}
                        </CustomBadge>
                        <IoArrowDown className="text-md text-black m-auto" />

                        {selectedState && (
                            <CustomBadge
                                className={cn(customBadgeClass, "cursor-pointer m-auto")}
                                rounded={badgeRounded}
                                size={badgeSize}
                                customColor={selectedState.color || undefined}
                            >
                                {selectedState.label || 'Seleccionar Estado'}
                            </CustomBadge>
                        )}
                    </div>
                    {hasComments && (
                        <div>
                            <Textarea
                                id="comments"
                                required={true}
                                label="Comentario"
                                placeholder="Escribe un comentario"
                                rows={3}
                                className={`col-span-1 md:col-span-2 [&>label>span]:font-medium p-4 `}
                                error={commentError}
                                onChange={(e) => setCommentError(e.target.value.length > 0 ? '' : 'Este campo es obligatorio')}
                            />
                        </div>
                    )}

                    <div className="flex justify-center p-3 gap-4">
                        <button className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-300" onClick={handleConfirm}>
                            {confirmBtnText}
                        </button>
                        <button className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600" onClick={handleCancel}>
                            {cancelBtnText}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default StatusDropdown;